import mapboxgl from "mapbox-gl";
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { MapLayers, ToggleStyleControl } from "../common/MapLayers/MapLayers";

const ACCESS_TOKEN = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

mapboxgl.accessToken = ACCESS_TOKEN;

const MarkerInfo = ({ unit, onDeleteUnit }) => (
  <div className="marker-info d-flex flex-column bg-dark p-2">
    <div className="row mb-2 pb-1">
      <div className="col-6 white-font h_4_5 fw-medium">ID</div>
      <div className="col-6 primary-font h_4_5 fw-semibold">
        {unit.unit_name}
      </div>
    </div>
    <div className="row mb-2 pb-1">
      <div className="col-6 white-font h_4_5 fw-medium">Status</div>
      <div
        className={`col-6 fw-semibold h_4_5 ${
          unit.status === "ACTIVE" ? "text-active" : "text-danger"
        }`}
      >
        {unit.status}
      </div>
    </div>
    <div className="row mb-2 pb-1">
      <div className="col-6 white-font h_4_5 fw-medium">Battery Volt</div>
      <div className="col-6 primary-font h_4_5 fw-semibold">
        {unit.battery_voltage} Volt
      </div>
    </div>
    <div className="row mb-2 pb-1">
      <div className="col-6 white-font h_4_5 fw-medium">Battery (%)</div>
      <div className="col-6 primary-font h_4_5 fw-semibold">
        {unit.btry_volt_per}%
      </div>
    </div>
    <div className="row mb-2 pb-1">
      <div className="col-6 white-font h_4_5 fw-medium">Power</div>
      <div className="col-6 primary-font h_4_5 fw-semibold">
        {unit.output} Watt
      </div>
    </div>
    <div className="row mb-2 pb-1">
      <div className="col-6 white-font h_4_5 fw-medium">Coordinates</div>
      <div className="col-6 primary-font h_4_5 fw-semibold">
        {parseFloat(unit.lat).toFixed(1)}, {parseFloat(unit.lon).toFixed(1)}
      </div>
    </div>
    <div className="row mb-2 pb-1">
      <div className="col-6 white-font h_4_5 fw-medium">Type</div>
      <div className="col-6 primary-font h_4_5 fw-semibold">
        {unit.unit_type}
      </div>
    </div>
    <div className="d-flex align-items-center gap-2">
      <a
        href={`/unit-details/${unit.id}`}
        className="btn marker-info-btn p-2 w-50 fs-7"
      >
        View Detail
      </a>
      <button
        disabled={unit.is_default}
        onClick={onDeleteUnit}
        className="btn marker-info-btn p-2 w-50 fs-7"
      >
        Delete Unit
      </button>
    </div>
  </div>
);

const UnitMarker = memo(({ unit, onSelect, selected, onDeleteUnit }) => {
  const markerRef = useRef(null);
  const popupRef = useRef(null);
  const popupContainerRef = useRef(null);
  const rootRef = useRef(null);

  const handleMouseOver = useCallback(() => onSelect(unit), [unit, onSelect]);
  const handleDeleteUnit = useCallback(
    () => onDeleteUnit(unit.id),
    [unit, onDeleteUnit]
  );

  const showPopup = useCallback(
    (e) => {
      if (e) e.stopPropagation();
      popupContainerRef.current = document.createElement("div");
      rootRef.current = createRoot(popupContainerRef.current);
      rootRef.current.render(
        <MarkerInfo unit={unit} onDeleteUnit={handleDeleteUnit} />
      );
      popupRef.current = new mapboxgl.Popup({
        offset: 1,
        closeButton: true,
        closeOnClick: false,
        anchor: "top",
      })
        .setLngLat([parseFloat(unit.lon), parseFloat(unit.lat)])
        .setDOMContent(popupContainerRef.current)
        .addTo(window.mapboxMap);

      if (markerRef.current) {
        markerRef.current.getElement().style.cursor = "default";
      }

      popupRef.current.on("close", () => {
        if (markerRef.current) {
          markerRef.current.getElement().style.cursor = "pointer";
        }
      });
    },
    [handleDeleteUnit, unit]
  );

  const markerClickHandler = useCallback(
    (e) => {
      e.stopPropagation();
      if (!popupRef.current) {
        showPopup(e);
      }
      handleMouseOver();
    },
    [handleMouseOver, showPopup]
  );

  useEffect(() => {
    if (!window.mapboxMap) return;
    if (markerRef.current) markerRef.current.remove();

    const marker = new mapboxgl.Marker({ color: "#ffe79b" })
      .setLngLat([parseFloat(unit.lon), parseFloat(unit.lat)])
      .addTo(window.mapboxMap);
    markerRef.current = marker;

    const el = marker.getElement();
    el.style.cursor = selected ? "default" : "pointer";
    el.addEventListener("click", markerClickHandler);

    if (selected) showPopup();

    return () => {
      el.removeEventListener("click", markerClickHandler);
      markerRef.current = null;
      popupContainerRef.current = null;
      if (markerRef.current) markerRef.current.remove();
      if (rootRef.current) {
        const root = rootRef.current;
        queueMicrotask(() => root.unmount());
        rootRef.current = null;
      }
      if (popupRef.current) {
        popupRef.current.remove();
      }
    };
  }, [
    unit,
    selected,
    handleMouseOver,
    handleDeleteUnit,
    markerClickHandler,
    showPopup,
  ]);

  return null;
});

export const MapSection = memo(
  ({ units, selectedMarker, onMarkerSelect, onMapClick, onDeleteUnit }) => {
    const mapContainer = useRef(null);
    const map = useRef(null);
    const [isMapLoaded, setIsMapLoaded] = useState(false);
    const [is3D, setIs3D] = useState(false);
    const [baseStyle, setBaseStyle] = useState(
      localStorage.getItem("baseStyle") || "satellite-streets"
    );
    const mapStyle = `mapbox://styles/mapbox/${baseStyle}-${
      is3D
        ? baseStyle === "dark" || baseStyle === "light"
          ? "v11"
          : "v12"
        : "v9"
    }`;

    const handleToggleMap = () => {
      setIs3D((prev) => {
        const newVal = !prev;
        localStorage.setItem("map_projection", newVal ? true : false);
        return newVal;
      });
    };

    useEffect(() => {
      if (map.current) return;
      const savedProjection = localStorage.getItem("map_projection");
      const isSaved3D = savedProjection === "true";
      setIs3D(isSaved3D);

      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: mapStyle,
        center: [
          parseFloat(units?.results?.[0]?.lon) || 10.4515,
          parseFloat(units?.results?.[0]?.lat) || 51.1657,
        ],
        zoom: 6,
        telemetry: false,
        attributionControl: false,
      });

      map.current.addControl(new mapboxgl.NavigationControl());
      map.current.addControl(
        MapLayers(setBaseStyle, baseStyle),
        "bottom-right"
      );
      const toggleControl = new ToggleStyleControl(handleToggleMap);
      map.current.addControl(toggleControl, "bottom-right");
      map.current.scrollZoom.enable();
      window.mapboxMap = map.current;

      map.current.on("load", () => setIsMapLoaded(true));

      map.current.on("click", async (e) => {
        const { lng, lat } = e.lngLat;
        try {
          const res = await fetch(
            `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${ACCESS_TOKEN}`
          );
          const data = await res.json();
          const context = data.features[0]?.context || [];
          const place = context.find((c) => c.id.includes("locality"))?.text;
          const city = context.find((c) => c.id.includes("place"))?.text;
          const country = context.find((c) => c.id.includes("country"))?.text;
          onMapClick({
            lat,
            lng,
            address:
              place && city && country
                ? `${place}, ${city}, ${country}`
                : city && country
                ? `${city}, ${country}`
                : city ||
                  country ||
                  `Location at ${lat.toFixed(4)}, ${lng.toFixed(4)}`,
          });
        } catch {
          onMapClick({ lat, lng, address: "Error getting location details" });
        }
      });

      return () => {
        if (map.current) map.current.remove();
        window.mapboxMap = null;
        map.current = null;
      };
    }, [units, onMapClick]);

    useEffect(() => {
      if (!map.current || !selectedMarker) return;

      const exists = units?.results?.some((u) => u.id === selectedMarker.id);
      if (!exists) return;

      map.current.flyTo({
        center: [
          parseFloat(selectedMarker.lon),
          parseFloat(selectedMarker.lat),
        ],
        zoom: 12,
        essential: true,
      });
    }, [selectedMarker, units]);

    useEffect(() => {
      if (map.current) {
        let newStyle = "";
        if (baseStyle === "dark" || baseStyle === "light") {
          newStyle = `mapbox://styles/mapbox/${baseStyle}-${
            is3D ? "v11" : "v9"
          }`;
        } else {
          newStyle = `mapbox://styles/mapbox/${baseStyle}-${
            is3D ? "v12" : "v9"
          }`;
        }
        map.current.setStyle(newStyle);
      }
    }, [baseStyle, is3D]);

    return (
      <div ref={mapContainer} style={{ height: "100%" }}>
        {isMapLoaded &&
          units?.results
            ?.filter(
              (u) => !isNaN(parseFloat(u.lat)) && !isNaN(parseFloat(u.lon))
            )
            .map((unit, idx) => (
              <UnitMarker
                key={unit.id || idx}
                unit={{
                  ...unit,
                  lat: parseFloat(unit.lat),
                  lon: parseFloat(unit.lon),
                }}
                onSelect={onMarkerSelect}
                selected={selectedMarker?.unit_name === unit.unit_name}
                onDeleteUnit={onDeleteUnit}
              />
            ))}
      </div>
    );
  }
);
